import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { validateRequired } from '../../../utils/validators';
import { ROUTES } from '../../../config/constants';
import './LoginForm.css';

const LoginForm = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [loginError, setLoginError] = useState('');

    // Lade loginError aus localStorage beim Mount
    useEffect(() => {
        const storedError = localStorage.getItem('loginError');
        if (storedError) {
            setLoginError(storedError);
        }
    }, []);

    const validateForm = () => {
        const newErrors = {};

        const usernameValidation = validateRequired(formData.username, 'Username');
        if (!usernameValidation.isValid) {
            newErrors.username = usernameValidation.error;
        }

        const passwordValidation = validateRequired(formData.password, 'Password');
        if (!passwordValidation.isValid) {
            newErrors.password = passwordValidation.error;
        }

        setErrors({
            username: newErrors.username || '',
            password: newErrors.password || ''
        });

        return Object.keys(newErrors).length === 0;
    };

    const clearLoginError = () => {
        setLoginError('');
        localStorage.removeItem('loginError');
    };

    const setLoginErrorPersistent = (error) => {
        setLoginError(error);
        localStorage.setItem('loginError', error);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear validation error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Clear login error when user starts typing
        if ((loginError || localStorage.getItem('loginError')) && value.length > 0) {
            clearLoginError();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearLoginError();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const result = await login(formData);

            if (result.success) {
                navigate(ROUTES.TENANT_SELECTION);
            } else {
                setLoginErrorPersistent(result.error || 'Login fehlgeschlagen');
            }
        } catch (err) {
            setLoginErrorPersistent('Ein Fehler ist aufgetreten');
        } finally {
            setLoading(false);
        }
    };

    const handleInitPassword = () => {
        navigate('/init-password');
    };

    const handleForgotPassword = () => {
        navigate('/');
    };

    // Verwende localStorage als Fallback
    const currentError = loginError || localStorage.getItem('loginError') || '';

    return (
        <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
                <input
                    type="text"
                    name="username"
                    placeholder="User Name / E-Mail"
                    value={formData.username}
                    onChange={handleChange}
                    className={errors.username ? 'error' : ''}
                    disabled={loading}
                />
                {errors.username && <span className="error-text">{errors.username}</span>}
            </div>

            <div className="form-group">
                <input
                    type="password"
                    name="password"
                    placeholder="Passwort"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password ? 'error' : ''}
                    disabled={loading}
                />
                {errors.password && <span className="error-text">{errors.password}</span>}
            </div>

            <div className="password-row">
                <button
                    type="button"
                    className="password-init-link"
                    onClick={handleInitPassword}
                    disabled={loading}
                >
                    Passwort initialisieren
                </button>

                <button
                    type="button"
                    className="forgot-password-link"
                    onClick={handleForgotPassword}
                    disabled={loading}
                >
                    Passwort vergessen?
                </button>
            </div>

            {/* Login-Fehler anzeigen */}
            {currentError && (
                <div className="error-message">
                    {currentError}
                </div>
            )}

            <button
                type="submit"
                className="submit-button"
                disabled={loading}
            >
                {loading ? 'Anmelden...' : 'Login'}
            </button>
        </form>
    );
};

export default LoginForm;